import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Z_CONSENT_GATE } from "../../lib/zLayers";
import { useConsentStore } from "../../store/consentStore";

export const PRIMARY_BUTTON =
  "h-10 min-w-[120px] flex-1 rounded-full border-none bg-[var(--primary)] px-4 text-sm font-semibold text-[var(--primary-foreground)] hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40";
export const SECONDARY_BUTTON =
  "h-10 min-w-[120px] flex-1 rounded-full border border-[var(--border)] bg-[var(--bg-surface)] px-4 text-sm font-semibold text-[var(--foreground)] hover:bg-[var(--bg-hover)] disabled:cursor-not-allowed disabled:opacity-40";
export const LINK_BUTTON =
  "border-none bg-transparent p-0 text-sm font-semibold text-[var(--primary-text)] underline cursor-pointer";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)];
}

/**
 * Makes everything in <body> except `keep` inert (no focus, no clicks, hidden from assistive tech) and returns the
 * undo. Elements that were already inert (e.g. under another open dialog) are left as they were.
 */
function inertOthers(keep: HTMLElement): () => void {
  const changed: Element[] = [];
  for (const el of Array.from(document.body.children)) {
    if (el === keep || el.hasAttribute("inert")) continue;
    el.setAttribute("inert", "");
    changed.push(el);
  }
  return () => changed.forEach((el) => el.removeAttribute("inert"));
}

/**
 * The modal shell of the consent screens (Figma "Privacy – Consent", web modals): a dimmed backdrop over whatever is
 * behind, one card in the middle. It is not dismissible (no backdrop click, no Escape): the user answers.
 * - Rendered in a portal on <body>; the rest of the page is `inert` while it is open.
 * - Focus moves into the dialog on open, Tab / Shift+Tab stay inside, and focus returns to where it was on close.
 * - While it is open the storage banner stays hidden, so one consent screen shows at a time.
 */
export default function ConsentDialog({
  testId,
  title,
  children,
}: {
  testId: string;
  title: string;
  children: React.ReactNode;
}) {
  const setGateOpen = useConsentStore((s) => s.setGateOpen);
  const hostRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setGateOpen(true);
    return () => setGateOpen(false);
  }, [setGateOpen]);

  useEffect(() => {
    const host = hostRef.current;
    const dialog = dialogRef.current;
    if (!host || !dialog) return;
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const undoInert = inertOthers(host);
    dialog.focus();
    return () => {
      undoInert();
      if (before && before.isConnected) before.focus();
    };
  }, []);

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key !== "Tab" || !dialogRef.current) return;
    const list = focusables(dialogRef.current);
    if (list.length === 0) {
      e.preventDefault();
      return;
    }
    const first = list[0];
    const last = list[list.length - 1];
    const active = document.activeElement;
    const inside = active instanceof Node && dialogRef.current.contains(active);
    if (e.shiftKey && (active === first || active === dialogRef.current || !inside)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && (active === last || !inside)) {
      e.preventDefault();
      first.focus();
    }
  }

  return createPortal(
    <div
      ref={hostRef}
      className="fixed inset-0 flex items-center justify-center overflow-y-auto bg-black/40 px-4 py-6"
      style={{ zIndex: Z_CONSENT_GATE }}
    >
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        data-testid={testId}
        onKeyDown={onKeyDown}
        className="w-full max-w-[560px] rounded-2xl bg-[var(--bg-surface)] p-6 shadow-lg outline-none"
      >
        {children}
      </section>
    </div>,
    document.body,
  );
}

export function DialogTitle({ children }: { children: React.ReactNode }) {
  return <h2 className="m-0 text-xl font-bold text-[var(--foreground)]">{children}</h2>;
}

export function DialogText({ children }: { children: React.ReactNode }) {
  return <p className="mt-2 mb-0 text-sm text-[var(--foreground-muted)]">{children}</p>;
}

export function DialogError({ testId, children }: { testId: string; children: React.ReactNode }) {
  return (
    <p role="alert" data-testid={testId} className="mt-3 mb-0 text-sm text-[var(--destructive-text)]">
      {children}
    </p>
  );
}
